import { Routes, Route, useParams, Outlet, Navigate, useLocation } from 'react-router-dom'
import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { supportedLanguages, defaultLanguage, type SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { CookieBanner } from '@/components/CookieBanner'
import { ChatBot } from '@/components/ChatBot'
import { ChatBotProvider } from '@/contexts/ChatBotContext'

// Pages
import { Home } from '@/pages/Home'
import { About } from '@/pages/About'
import { Method } from '@/pages/Method'
import { Products } from '@/pages/Products'
import { ProductDetail } from '@/pages/ProductDetail'
import { Careers } from '@/pages/Careers'
import { Skills } from '@/pages/Skills'
import { Contacts } from '@/pages/Contacts'
import { Privacy } from '@/pages/Privacy'
import { Cookies } from '@/pages/Cookies'

// Map route keys to their page components
const routeComponents: Record<string, ReactNode> = {
  about: <About />,
  method: <Method />,
  products: <Products />,
  'product-outsourcing': <ProductDetail productId="product-outsourcing" />,
  'product-machining': <ProductDetail productId="product-machining" />,
  'product-transmission': <ProductDetail productId="product-transmission" />,
  'product-plastics': <ProductDetail productId="product-plastics" />,
  'product-assembly': <ProductDetail productId="product-assembly" />,
  careers: <Careers />,
  skills: <Skills />,
  contacts: <Contacts />,
  privacy: <Privacy />,
  cookies: <Cookies />,
}

// Generate all unique paths for each route key across all languages
function getAllLocalizedRoutes() {
  const routes: { path: string; element: ReactNode }[] = []

  for (const [routeKey, langPaths] of Object.entries(localizedRoutes)) {
    if (routeKey === 'home') continue // home is handled by index route
    const component = routeComponents[routeKey]
    if (!component) continue

    // Collect unique paths to avoid duplicate routes
    const uniquePaths = new Set(Object.values(langPaths).filter(Boolean))
    for (const path of uniquePaths) {
      routes.push({ path, element: component })
    }
  }

  return routes
}

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  return <Outlet />
}

const allRoutes = getAllLocalizedRoutes()

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Redirect root to default language */}
        <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

        {/* Language-prefixed routes */}
        <Route path="/:lang" element={<LanguageWrapper />}>
          <Route index element={<Home />} />
          {allRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>
        <Route path="*" element={<Navigate to={`/${defaultLanguage}`} replace />} />
      </Routes>
    </>
  )
}

function App() {
  return (
    <ChatBotProvider>
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow">
          <AppRoutes />
        </div>
        <Footer />
        <ChatBot />
        <CookieBanner />
      </div>
    </ChatBotProvider>
  )
}

export default App
